import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { AppSwitcher } from './AppSwitcher.tsx';
import { ThemeToggle } from './ThemeToggle.tsx';
import { Icon } from './Icon.tsx';
import { dv } from '../i18n/dv.ts';

// Contextual screen title + whether this screen shows a back button (i.e. it's a
// detail/form screen rather than a primary bottom-nav destination). Mirrors
// WapSign's mobile top app bar.
function screenFor(pathname: string): { title: string; back: boolean } {
  if (pathname === '/') return { title: dv.nav.dashboard, back: false };
  if (pathname === '/letters') return { title: dv.nav.letters, back: false };
  if (pathname.startsWith('/letters/')) return { title: dv.nav.letters, back: true };
  if (pathname.startsWith('/settings')) return { title: dv.nav.settings, back: false };
  if (pathname === '/profile') return { title: dv.nav.profile, back: false };
  return { title: dv.appName, back: false };
}

// Material 3 mobile top app bar: back/logo, contextual title, then the app
// controls (ecosystem switcher, theme, admin settings and sign-out).
export function Header(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { title, back } = screenFor(pathname);

  return (
    <header
      className="md:hidden sticky top-0 z-30 bg-[hsl(var(--chrome))]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="flex h-14 items-center gap-1 px-1.5">
        {back ? (
          <Link
            to="/letters"
            aria-label={dv.common.back}
            className="grid size-11 place-items-center rounded-full text-foreground active:bg-muted/60"
          >
            {/* RTL: the back arrow points to the inline-start (right). */}
            <Icon name="arrow_forward" size={24} />
          </Link>
        ) : (
          <Link to="/" aria-label={dv.nav.dashboard} className="grid size-11 place-items-center">
            <img src="/icons/wapdocs-mark-192.png" alt="" className="size-8 object-contain" />
          </Link>
        )}

        <h1 className="flex-1 min-w-0 truncate text-[1.3rem] font-medium text-ink">{title}</h1>

        {user && (
          <div className="flex items-center gap-0.5 shrink-0">
            <AppSwitcher variant="header" />
            <ThemeToggle />
            {user.isOrgAdmin && (
              <Link
                to="/settings"
                aria-label={dv.nav.settings}
                className="grid size-11 place-items-center rounded-full text-muted-foreground active:bg-muted/60"
              >
                <Icon name="tune" size={20} />
              </Link>
            )}
            <button
              onClick={logout}
              aria-label={dv.auth.logout}
              className="grid size-11 place-items-center rounded-full text-muted-foreground active:bg-muted/60"
            >
              <Icon name="logout" size={20} />
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
